import { useState, useEffect, useCallback } from "react";
import { FaTimes, FaBan, FaCheckCircle } from "react-icons/fa";
import adminCalendarService from "../../services/adminCalendarService";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  confirmed: "bg-blue-100   text-blue-800   border-blue-300",
  completed: "bg-green-100  text-green-800  border-green-300",
  cancelled: "bg-red-100    text-red-800    border-red-300",
};

const EXT_PLATFORMS = [
  {
    id: "airbnb",
    label: "Airbnb",
    color: "bg-rose-100 text-rose-700 border-rose-300",
  },
  {
    id: "booking.com",
    label: "Booking.com",
    color: "bg-blue-100 text-blue-800 border-blue-300",
  },
  {
    id: "viator",
    label: "Viator",
    color: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
  {
    id: "tripadvisor",
    label: "TripAdvisor",
    color: "bg-teal-100 text-teal-700 border-teal-300",
  },
  {
    id: "getyourguide",
    label: "GYG",
    color: "bg-orange-100 text-orange-700 border-orange-300",
  },
  {
    id: "direct",
    label: "Direct",
    color: "bg-sky-100 text-sky-700 border-sky-300",
  },
  {
    id: "other",
    label: "Other",
    color: "bg-gray-100 text-gray-700 border-gray-300",
  },
];
const EXT_PLATFORM_MAP = Object.fromEntries(
  EXT_PLATFORMS.map((p) => [p.id, p]),
);

const SERVICE_DEFAULT_CAP = {
  speedboat: 11,
  bayliner: 5,
  "jet-ski": 2,
  "luxury-yacht": 11,
};

function getServiceTimeSlotOptions(serviceId) {
  if (serviceId === "jet-ski") {
    return [
      { value: "09:00 - 10:00", label: "Slot 1 · 09:00 – 10:00" },
      { value: "11:00 - 12:00", label: "Slot 2 · 11:00 – 12:00" },
      { value: "16:00 - 17:00", label: "Slot 3 · 16:00 – 17:00" },
      { value: "18:00 - 19:00", label: "Slot 4 · 18:00 – 19:00" },
    ];
  }
  if (serviceId === "luxury-yacht") {
    return [
      { value: "10:00 - 14:00", label: "Morning Charter · 10:00 – 14:00" },
      { value: "16:00 - 20:00", label: "Sunset Charter · 16:00 – 20:00" },
    ];
  }
  if (serviceId === "bayliner") {
    return [
      { value: "09:00 - 09:30", label: "30-min · 09:00 – 09:30" },
      { value: "09:00 - 10:00", label: "60-min · 09:00 – 10:00" },
      { value: "10:00 - 10:30", label: "30-min · 10:00 – 10:30" },
      { value: "11:00 - 11:30", label: "30-min · 11:00 – 11:30" },
      { value: "11:00 - 12:00", label: "60-min · 11:00 – 12:00" },
      { value: "14:00 - 14:30", label: "30-min · 14:00 – 14:30" },
      { value: "14:00 - 15:00", label: "60-min · 14:00 – 15:00" },
      { value: "15:00 - 15:30", label: "30-min · 15:00 – 15:30" },
      { value: "16:00 - 16:30", label: "30-min · 16:00 – 16:30" },
      { value: "16:00 - 17:00", label: "60-min · 16:00 – 17:00" },
      { value: "17:00 - 17:30", label: "30-min · 17:00 – 17:30" },
    ];
  }
  // speedboat — all 8 slots across all 4 packages
  return [
    { value: "09:00 - 10:00", label: "Express · 09:00 – 10:00" },
    { value: "09:00 - 10:30", label: "Morning Tour · 09:00 – 10:30" },
    { value: "09:00 - 11:00", label: "Extended · 09:00 – 11:00" },
    { value: "11:00 - 12:00", label: "Midday Express · 11:00 – 12:00" },
    { value: "15:00 - 16:00", label: "Afternoon Express · 15:00 – 16:00" },
    { value: "17:00 - 18:30", label: "Evening Tour · 17:00 – 18:30" },
    { value: "18:00 - 19:00", label: "Late Express · 18:00 – 19:00" },
    { value: "18:00 - 20:00", label: "Late Extended · 18:00 – 20:00" },
  ];
}

const SERVICES = [
  { id: "speedboat", label: "SpeedBoat Tour" },
  { id: "bayliner", label: "Bayliner Water Sports" },
  { id: "jet-ski", label: "Jet Ski Rental" },
  { id: "luxury-yacht", label: "Luxury Yacht" },
];

export default function DayDetailPanel({
  date,
  onClose,
  onDataChange,
  defaultService = "speedboat",
}) {
  const [bookings, setBookings] = useState([]);
  const [avail, setAvail] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [activeTab, setActiveTab] = useState("bookings"); // bookings | slots | external
  const [selectedSvc, setSelectedSvc] = useState(defaultService);
  const [newSlotTime, setNewSlotTime] = useState("10:00");
  const [newSlotCap, setNewSlotCap] = useState(
    SERVICE_DEFAULT_CAP[defaultService] ?? SERVICE_DEFAULT_CAP["speedboat"],
  );
  const [slotSaving, setSlotSaving] = useState(null); // slotTime being acted on
  const [slotBlockReason, setSlotBlockReason] = useState({});

  // External bookings
  const [externalBookings, setExternalBookings] = useState([]);
  const [extSaving, setExtSaving] = useState(false);
  const [extDeleting, setExtDeleting] = useState(null);
  const [extIsCustomSlot, setExtIsCustomSlot] = useState(false);
  const [extForm, setExtForm] = useState({
    platform: "airbnb",
    guestName: "",
    guestCount: 2,
    timeSlot: "",
    serviceId: defaultService,
    price: "",
    status: "confirmed",
    referenceId: "",
  });

  const dateStr = date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : null;

  const notify = (text, type = "info") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const load = useCallback(async () => {
    if (!dateStr) return;
    setLoading(true);
    try {
      const [bks, blockedList, av, extBks] = await Promise.all([
        adminCalendarService.getBookingsForDate(dateStr),
        adminCalendarService.getBlockedDates(),
        adminCalendarService.getOrCreateAvailability(selectedSvc, dateStr),
        adminCalendarService.getExternalBookingsForDate(dateStr),
      ]);
      setBookings(bks);
      setExternalBookings(extBks);
      const isBlocked = blockedList.some(
        (b) =>
          b.date === dateStr &&
          (b.serviceId === "all" || b.serviceId === selectedSvc),
      );
      setBlocked(isBlocked);
      setAvail(av);
    } catch (err) {
      notify("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [dateStr, selectedSvc]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleBlock = async () => {
    setSaving(true);
    try {
      if (blocked) {
        await adminCalendarService.unblockDate(dateStr, "all");
        notify("Date unblocked", "success");
      } else {
        await adminCalendarService.blockDates(
          [dateStr],
          blockReason || "Admin blocked",
          "all",
        );
        notify("Date blocked", "success");
      }
      setBlocked(!blocked);
      if (onDataChange) onDataChange();
    } catch (err) {
      notify("Error toggling block: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSlotCapChange = async (slotTime, newCap) => {
    setSaving(true);
    try {
      await adminCalendarService.updateSlotCapacity(
        selectedSvc,
        dateStr,
        slotTime,
        Number(newCap),
      );
      notify("Capacity updated", "success");
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSlot = async () => {
    setSaving(true);
    try {
      await adminCalendarService.addTimeSlot(
        selectedSvc,
        dateStr,
        newSlotTime,
        Number(newSlotCap),
      );
      notify("Slot added", "success");
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSlot = async (slotTime) => {
    if (!window.confirm(`Remove time slot ${slotTime}?`)) return;
    setSaving(true);
    try {
      await adminCalendarService.removeTimeSlot(selectedSvc, dateStr, slotTime);
      notify("Slot removed", "success");
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSlotBlock = async (slotTime, isCurrentlyBlocked) => {
    setSlotSaving(slotTime);
    try {
      if (isCurrentlyBlocked) {
        await adminCalendarService.unblockSlot(selectedSvc, dateStr, slotTime);
        notify(`Slot ${slotTime} unblocked`, "success");
      } else {
        const reason = slotBlockReason[slotTime] || "Admin blocked";
        await adminCalendarService.blockSlot(
          selectedSvc,
          dateStr,
          slotTime,
          reason,
        );
        notify(`Slot ${slotTime} blocked`, "success");
      }
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSlotSaving(null);
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    setSaving(true);
    try {
      if (action === "confirm")
        await adminCalendarService.confirmBooking(bookingId);
      if (action === "complete")
        await adminCalendarService.completeBooking(bookingId);
      if (action === "cancel")
        await adminCalendarService.cancelBooking(bookingId);
      if (action === "delete") {
        if (!window.confirm("Permanently delete this booking?")) {
          setSaving(false);
          return;
        }
        await adminCalendarService.deleteBooking(bookingId);
      }
      notify("Booking updated", "success");
      load();
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!dateStr) return null;

  const displayDate = date
    ? date.toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : dateStr;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Top accent bar */}
        <div
          className={`h-1 ${blocked ? "bg-red-500" : "bg-gradient-to-r from-sky-400 to-teal-400"}`}
        />
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b bg-slate-800">
          <div>
            <h2 className="text-base font-bold text-white">{displayDate}</h2>
            {blocked && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-300 mt-0.5">
                <FaBan className="text-xs" /> Date is blocked
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition"
          >
            <FaTimes />
          </button>
        </div>

        {/* Notification */}
        {msg && (
          <div
            className={`mx-6 mt-3 px-4 py-2 rounded-lg text-sm font-medium border
            ${
              msg.type === "success"
                ? "bg-green-50 text-green-800 border-green-300"
                : msg.type === "error"
                  ? "bg-red-50   text-red-800   border-red-300"
                  : "bg-blue-50 text-blue-800 border-blue-300"
            }`}
          >
            {msg.text}
          </div>
        )}

        {/* Block / Unblock */}
        <div className="px-4 sm:px-6 py-2.5 bg-slate-50 border-b flex flex-wrap items-center gap-3">
          {!blocked && (
            <input
              className="flex-1 min-w-[160px] border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="Block reason (optional)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            />
          )}
          <button
            onClick={handleToggleBlock}
            disabled={saving}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition
              ${
                blocked
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
          >
            {blocked ? (
              <>
                <FaCheckCircle className="text-xs" /> Unblock Date
              </>
            ) : (
              <>
                <FaBan className="text-xs" /> Block Date
              </>
            )}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-white overflow-x-auto">
          {[
            {
              id: "bookings",
              label: `Bookings (${bookings.filter((b) => b.serviceId === selectedSvc).length})`,
            },
            {
              id: "external",
              label: `External (${externalBookings.filter((b) => b.serviceId === selectedSvc).length})`,
            },
            { id: "slots", label: "Time Slots" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition
                ${
                  activeTab === t.id
                    ? "border-sky-500 text-sky-700 bg-sky-50/50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-400">
              Loading…
            </div>
          ) : activeTab === "external" ? (
            /* ── EXTERNAL BOOKINGS TAB ── */
            <div className="space-y-4">
              {/* Quick-add form */}
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-sky-800 mb-3">
                  + Quick Add External Booking
                </p>
                <div className="space-y-3">
                  {/* Platform pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {EXT_PLATFORMS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() =>
                          setExtForm((f) => ({ ...f, platform: p.id }))
                        }
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition
                          ${extForm.platform === p.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-gray-600 border-gray-300 hover:border-sky-400"}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Guest name *"
                      value={extForm.guestName}
                      onChange={(e) =>
                        setExtForm((f) => ({ ...f, guestName: e.target.value }))
                      }
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    />
                    <input
                      type="text"
                      placeholder="Platform reference"
                      value={extForm.referenceId}
                      onChange={(e) =>
                        setExtForm((f) => ({
                          ...f,
                          referenceId: e.target.value,
                        }))
                      }
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    />
                    <select
                      value={extIsCustomSlot ? "__custom__" : extForm.timeSlot}
                      onChange={(e) => {
                        if (e.target.value === "__custom__") {
                          setExtIsCustomSlot(true);
                          setExtForm((f) => ({ ...f, timeSlot: "" }));
                        } else {
                          setExtIsCustomSlot(false);
                          setExtForm((f) => ({
                            ...f,
                            timeSlot: e.target.value,
                          }));
                        }
                      }}
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    >
                      <option value="">— Any slot —</option>
                      {getServiceTimeSlotOptions(extForm.serviceId).map(
                        (opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ),
                      )}
                      <option value="__custom__">✏️ Custom time…</option>
                    </select>
                    {extIsCustomSlot && (
                      <input
                        type="text"
                        placeholder="e.g. 13:00 - 14:30"
                        value={extForm.timeSlot}
                        onChange={(e) =>
                          setExtForm((f) => ({
                            ...f,
                            timeSlot: e.target.value,
                          }))
                        }
                        className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 col-span-2"
                        autoFocus
                      />
                    )}
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={extForm.guestCount}
                      onChange={(e) =>
                        setExtForm((f) => ({
                          ...f,
                          guestCount: e.target.value,
                        }))
                      }
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                      placeholder="Guests"
                    />
                    <select
                      value={extForm.serviceId}
                      onChange={(e) =>
                        setExtForm((f) => ({ ...f, serviceId: e.target.value }))
                      }
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    >
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={extForm.price}
                      onChange={(e) =>
                        setExtForm((f) => ({ ...f, price: e.target.value }))
                      }
                      placeholder="Price €"
                      className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      if (!extForm.guestName.trim()) {
                        notify("Guest name required", "error");
                        return;
                      }
                      setExtSaving(true);
                      try {
                        await adminCalendarService.addExternalBooking({
                          ...extForm,
                          date: dateStr,
                        });
                        notify("External booking added", "success");
                        setExtForm({
                          platform: "airbnb",
                          guestName: "",
                          guestCount: 2,
                          timeSlot: "",
                          serviceId: defaultService,
                          price: "",
                          status: "confirmed",
                          referenceId: "",
                        });
                        setExtIsCustomSlot(false);
                        load();
                        if (onDataChange) onDataChange();
                      } catch (err) {
                        notify("Error: " + err.message, "error");
                      } finally {
                        setExtSaving(false);
                      }
                    }}
                    disabled={extSaving}
                    className="w-full py-2 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 transition"
                  >
                    {extSaving ? "Saving…" : "Add External Booking"}
                  </button>
                </div>
              </div>

              {/* List */}
              {externalBookings.filter((b) => b.serviceId === selectedSvc)
                .length === 0 ? (
                <p className="text-center text-gray-400 py-8">
                  No external bookings for this date.
                </p>
              ) : (
                <div className="space-y-2">
                  {externalBookings
                    .filter((b) => b.serviceId === selectedSvc)
                    .map((b) => {
                      const pl =
                        EXT_PLATFORM_MAP[b.platform] || EXT_PLATFORM_MAP.other;
                      return (
                        <div
                          key={b.id}
                          className="border rounded-xl p-3 bg-white shadow-sm flex items-center justify-between gap-3 flex-wrap"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pl.color}`}
                              >
                                {pl.label}
                              </span>
                              <span
                                className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[b.status] || STATUS_COLORS.confirmed} capitalize`}
                              >
                                {b.status}
                              </span>
                            </div>
                            <p className="font-medium text-gray-800 text-sm">
                              {b.guestName}
                            </p>
                            <p className="text-xs text-gray-400">
                              {SERVICES.find((s) => s.id === b.serviceId)
                                ?.label || b.serviceId}
                              {b.timeSlot ? ` · ${b.timeSlot}` : ""}
                              {" · "}
                              {b.guestCount} guest
                              {b.guestCount !== 1 ? "s" : ""}
                              {b.price ? ` · €${b.price}` : ""}
                            </p>
                            {b.referenceId && (
                              <p className="text-xs text-gray-400 font-mono">
                                Ref: {b.referenceId}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={async () => {
                              if (
                                !window.confirm("Delete this external booking?")
                              )
                                return;
                              setExtDeleting(b.id);
                              try {
                                await adminCalendarService.deleteExternalBooking(
                                  b.id,
                                );
                                notify("Deleted", "success");
                                load();
                                if (onDataChange) onDataChange();
                              } catch (err) {
                                notify("Error: " + err.message, "error");
                              } finally {
                                setExtDeleting(null);
                              }
                            }}
                            disabled={extDeleting === b.id}
                            className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 disabled:opacity-50 flex-shrink-0"
                          >
                            {extDeleting === b.id ? "…" : "Delete"}
                          </button>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          ) : activeTab === "bookings" ? (
            bookings.filter((b) => b.serviceId === selectedSvc).length === 0 ? (
              <p className="text-center text-gray-400 py-12">
                No bookings for this date.
              </p>
            ) : (
              <div className="space-y-3">
                {bookings
                  .filter((b) => b.serviceId === selectedSvc)
                  .map((b) => (
                    <div
                      key={b.id}
                      className="border rounded-xl p-4 bg-white shadow-sm"
                    >
                      <div className="flex flex-wrap items-start gap-3 justify-between">
                        <div>
                          <p className="font-semibold text-gray-800">
                            {b.bookingReference || b.id.slice(0, 8)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {b.customerInfo?.name ||
                              b.customerInfo?.email ||
                              "Guest"}{" "}
                            · {b.numberOfGuests} guests
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {b.serviceType}{" "}
                            {b.timeSlot ? `· ${b.timeSlot}` : ""}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[b.status] || STATUS_COLORS.pending}`}
                          >
                            {b.status}
                          </span>
                          <span className="text-sm font-bold text-gray-700">
                            €{b.totalPrice}
                          </span>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                        {b.status === "pending" && (
                          <button
                            onClick={() => handleBookingAction(b.id, "confirm")}
                            className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Confirm
                          </button>
                        )}
                        {(b.status === "confirmed" ||
                          b.status === "pending") && (
                          <button
                            onClick={() =>
                              handleBookingAction(b.id, "complete")
                            }
                            className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            Complete
                          </button>
                        )}
                        {b.status !== "cancelled" &&
                          b.status !== "completed" && (
                            <button
                              onClick={() =>
                                handleBookingAction(b.id, "cancel")
                              }
                              className="px-3 py-1 text-xs font-medium bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                            >
                              Cancel
                            </button>
                          )}
                        <button
                          onClick={() => handleBookingAction(b.id, "delete")}
                          className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )
          ) : (
            /* SLOTS TAB */
            <div className="space-y-5">
              {/* Service selector */}
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSelectedSvc(s.id);
                      setNewSlotCap(SERVICE_DEFAULT_CAP[s.id] ?? 11);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition
                      ${
                        selectedSvc === s.id
                          ? "bg-sky-600 text-white border-sky-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-sky-400"
                      }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Existing slots */}
              <div className="space-y-2">
                {(avail?.timeSlots || []).map((slot) => {
                  const fillPct =
                    slot.capacity > 0
                      ? Math.round((slot.booked / slot.capacity) * 100)
                      : 0;
                  const isSlotBlocked = (avail?.blockedSlots || []).includes(
                    slot.time,
                  );
                  const isSavingThis = slotSaving === slot.time;
                  const reasonKey = slot.time.replace(":", "_");
                  const blockReason = avail?.slotBlockReasons?.[reasonKey];
                  return (
                    <div
                      key={slot.time}
                      className={`border rounded-xl p-3 transition ${
                        isSlotBlocked
                          ? "bg-red-50 border-red-300"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      {/* Top row: time + blocked badge + actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`w-14 font-mono font-bold text-sm ${
                            isSlotBlocked
                              ? "text-red-600 line-through"
                              : "text-gray-700"
                          }`}
                        >
                          {slot.time}
                        </span>

                        {isSlotBlocked && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300">
                            🚫 Blocked{blockReason ? ` · ${blockReason}` : ""}
                          </span>
                        )}

                        <div className="ml-auto flex items-center gap-2">
                          {/* Capacity input — disabled when blocked */}
                          <input
                            type="number"
                            min={slot.booked}
                            max={100}
                            defaultValue={slot.capacity}
                            disabled={isSlotBlocked}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && val !== slot.capacity)
                                handleSlotCapChange(slot.time, val);
                            }}
                            className={`w-16 border rounded px-2 py-1 text-sm text-center ${
                              isSlotBlocked
                                ? "opacity-40 cursor-not-allowed"
                                : ""
                            }`}
                            title="Capacity"
                          />

                          {/* Block / Unblock toggle */}
                          <button
                            onClick={() =>
                              handleToggleSlotBlock(slot.time, isSlotBlocked)
                            }
                            disabled={isSavingThis}
                            title={
                              isSlotBlocked ? "Unblock slot" : "Block slot"
                            }
                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition disabled:opacity-50 ${
                              isSlotBlocked
                                ? "bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
                                : "bg-red-100   text-red-700   border-red-300   hover:bg-red-200"
                            }`}
                          >
                            {isSavingThis
                              ? "…"
                              : isSlotBlocked
                                ? "✓ Unblock"
                                : "🚫 Block"}
                          </button>

                          {/* Remove slot button */}
                          <button
                            onClick={() => handleRemoveSlot(slot.time)}
                            disabled={isSlotBlocked}
                            className={`text-gray-400 hover:text-red-600 text-lg leading-none font-bold ${
                              isSlotBlocked
                                ? "opacity-30 cursor-not-allowed"
                                : ""
                            }`}
                            title="Remove slot"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {/* Block reason input — only shown when not yet blocked */}
                      {!isSlotBlocked && (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Block reason (optional)"
                            value={slotBlockReason[slot.time] || ""}
                            onChange={(e) =>
                              setSlotBlockReason((prev) => ({
                                ...prev,
                                [slot.time]: e.target.value,
                              }))
                            }
                            className="flex-1 border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-600 bg-white"
                          />
                        </div>
                      )}

                      {/* Fill bar — hidden when blocked */}
                      {!isSlotBlocked && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                            <span>
                              {slot.booked} / {slot.capacity} booked
                            </span>
                            <span
                              className={
                                fillPct >= 100
                                  ? "text-red-600 font-semibold"
                                  : fillPct >= 75
                                    ? "text-orange-600"
                                    : "text-green-600"
                              }
                            >
                              {fillPct}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${
                                fillPct >= 100
                                  ? "bg-red-500"
                                  : fillPct >= 75
                                    ? "bg-orange-400"
                                    : "bg-green-400"
                              }`}
                              style={{ width: `${Math.min(fillPct, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {avail?.timeSlots?.length === 0 && (
                  <p className="text-gray-400 text-sm text-center py-4">
                    No time slots defined.
                  </p>
                )}
              </div>

              {/* Add new slot */}
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Add Time Slot
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="time"
                    value={newSlotTime}
                    onChange={(e) => setNewSlotTime(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                  />
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={newSlotCap}
                    onChange={(e) => setNewSlotCap(Number(e.target.value))}
                    className="w-20 border rounded-lg px-3 py-1.5 text-sm text-center"
                    placeholder="Cap"
                  />
                  <button
                    onClick={handleAddSlot}
                    disabled={saving}
                    className="px-4 py-1.5 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 disabled:opacity-50"
                  >
                    + Add Slot
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
